import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import { useFetchPublicHaveListQuery, useFetchPublicWantListQuery } from 'store/services/collection/publicCollectionApi';
import UserNotFoundPage from './UserNotFoundPage';
import LoginPromptModal from 'components/LoginPromptModal';
import CardsTable from 'components/CardsTable';
import Footer from 'components/Footer';
import Pagination from 'components/Pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TextInputField from 'components/TextInputField';
import { SortOrder } from 'types/interfaces/sort';
import { MTGCard } from 'types/interfaces/mtgCard';
import { CollectionList } from 'types/interfaces/collectionList';
import { Theme, useTheme } from '@mui/material';
import { CardsTableCellsData } from 'components/CardsTable/types';
import { AssetUrls } from 'config/assets';
import { Points } from 'components/Points/Points';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { SetIcon } from 'components/SetIcon';
import './SharedCollection.css';

interface Props {
  tab: 'have-list' | 'want-list';
}

// Helper function to format conditions
const formatConditions = (acceptableConditions?: string[]): string => {
  if (!acceptableConditions || acceptableConditions.length === 0) return 'Any';

  const conditionMap: Record<string, string> = {
    'NEAR_MINT': 'NM',
    'SLIGHTLY_PLAYED': 'LP',
    'MODERATELY_PLAYED': 'MP',
    'HEAVILY_PLAYED': 'HP',
    'DAMAGED': 'DMG',
  };

  const mapped = acceptableConditions.map(cond => conditionMap[cond] || cond);

  if (mapped.length >= 5 || (mapped.includes('NM') && mapped.includes('LP') && mapped.includes('MP'))) {
    return 'Any';
  }

  return mapped.join(', ');
};

// Helper function to format foil
const formatFoilPreference = (acceptFoil?: boolean, acceptNonFoil?: boolean) => {
  if (acceptFoil && acceptNonFoil) {
    return 'Any';
  } else if (acceptFoil) {
    return (
      <img
        src={`${AssetUrls.base}/assets/add-cards/foil.png`}
        width={14.15}
        height={14.21}
        alt="foil-icon"
      />
    );
  }
  return '-';
};

// Helper function to format languages
const formatLanguages = (languages?: string[]): string => {
  if (!languages || languages.length === 0) return 'EN';

  const languageMap: Record<string, string> = {
    'english': 'EN', 'spanish': 'ES', 'french': 'FR', 'german': 'DE',
    'portuguese': 'PT', 'japanese': 'JA', 's. chinese': 'ZH-S', 't. chinese': 'ZH-T',
    'korean': 'KO', 'russian': 'RU', 'italian': 'IT',
    'en': 'EN', 'es': 'ES', 'fr': 'FR', 'de': 'DE', 'pt': 'PT', 'ja': 'JA',
    'ko': 'KO', 'ru': 'RU', 'it': 'IT', 'zh-hans': 'ZH-S', 'zh-hant': 'ZH-T'
  };

  return languages.map(lang => {
    const lower = lang.toLowerCase();
    return languageMap[lower] || lang.toUpperCase();
  }).join(', ');
};

// Helper function to get display price for a card
const getCardDisplayPrice = (row: CollectionList): number | null => {
  // Use backend-provided displayPrice if available (handles etched fallback)
  if (row.displayPrice != null) {
    return row.displayPrice;
  }

  // Fallback to card pricing fields
  if (!row.card) return null;

  // Cast to access point value fields that come from backend
  const cardWithPricing = row.card as any;

  // Prefer non-foil price if available, otherwise use foil price
  if (cardWithPricing.nonFoilPointValue && cardWithPricing.nonFoilPointValue > 0) {
    return cardWithPricing.nonFoilPointValue;
  }

  if (cardWithPricing.foilPointValue && cardWithPricing.foilPointValue > 0) {
    return cardWithPricing.foilPointValue;
  }

  if (cardWithPricing.pointValue && cardWithPricing.pointValue > 0) {
    return cardWithPricing.pointValue;
  }

  return null;
};

// Generate table cells data for read-only view
const getReadOnlyTableCells = (
  theme: Theme,
  rows: CollectionList[],
  isWantList: boolean = false,
  isHaveList: boolean = false,
  isMobile: boolean = false
): CardsTableCellsData => {
  const headCells = [
    { key: 'quantity', label: 'Quantity', columnWidth: isMobile ? 50 : 60 },
    { key: 'name', label: 'Name' },
    { key: 'setName', label: 'Set' },
  ];

  if (!isMobile) {
    headCells.push(
      { key: 'isFoil', label: 'Foil', columnWidth: 40 },
      { key: 'condition', label: 'Condition', columnWidth: 70 },
      { key: 'lang', label: 'Language', columnWidth: 60 }
    );
  }

  return {
    headCells,
    bodyRows: rows.map((row) => {
      const components = [
        <div className="collection-card-quantity-container">
          <div className="collection-card-quantity">{row.quantity}</div>
        </div>,
        <div className="collection-card-name">
          {row.card?.scryfallID && (
            <img
              src={AssetUrls.cards.getCardImage(row.card.scryfallID, 'art_crop')}
              className="collection-card-name-img"
              alt={`table-row-image-${row.cardName || row.card?.name}`}
              onError={(e) => {
                const img = e.currentTarget;
                const currentSrc = img.src;
                const scryfallId = row.card?.scryfallID;

                // For art_crop images, try different sizes if art_crop fails
                if (scryfallId && !img.dataset.formatFallbackComplete) {
                  if (currentSrc.includes('/art_crop/')) {
                    // Art crop failed, try normal
                    img.src = AssetUrls.cards.getCardImage(scryfallId, 'normal');
                    return;
                  } else if (currentSrc.includes('/normal/')) {
                    // Normal failed, try small
                    img.src = AssetUrls.cards.getCardImage(scryfallId, 'small');
                    return;
                  } else if (currentSrc.includes('/small/')) {
                    // Small failed, try large
                    img.src = AssetUrls.cards.getCardImage(scryfallId, 'large');
                    return;
                  } else if (currentSrc.includes('/large/')) {
                    // Large failed, mark as complete
                    img.dataset.formatFallbackComplete = 'true';
                  }
                }

                // If all fallbacks failed, use default card back
                img.src = AssetUrls.cards.defaultBack;
              }}
            />
          )}
          <span className="collection-card-name-text">
            {row.cardName || row.card?.name}
          </span>
        </div>,
        <div className="collection-card-set">
          <SetIcon setIconUrl={row.card?.setIconUrl} setName={row.card?.setName} inverted={false} />
          <span className="collection-card-set-text">
            {(() => {
              if (isHaveList) {
                if (row.card?.setName) {
                  return row.card.setName + (row.card.collectorNumber ? ' #' + row.card.collectorNumber : '');
                }
                return '';
              }
              if (isWantList && (!row.acceptableSets || row.acceptableSets.length === 0)) {
                return 'Any';
              }
              if (row.acceptableSets && row.acceptableSets.length > 0) {
                return row.acceptableSets.join(', ');
              }
              if (row.card?.setName) {
                return row.card.setName + (row.card.collectorNumber ? ' #' + row.card.collectorNumber : '');
              }
              return '';
            })()}
          </span>
        </div>,
      ];

      if (!isMobile) {
        components.push(
          <div>
            {isHaveList
              ? (row.foil ? <img src={`${AssetUrls.base}/assets/add-cards/foil.png`} width={14.15} height={14.21} alt="foil-icon" /> : '—')
              : formatFoilPreference(row.acceptFoil, row.acceptNonFoil)}
          </div>,
          <div>
            {isHaveList
              ? (row.condition ? (() => {
                  const conditionMap: Record<string, string> = {
                    'NEAR_MINT': 'NM', 'SLIGHTLY_PLAYED': 'LP', 'MODERATELY_PLAYED': 'MP',
                    'HEAVILY_PLAYED': 'HP', 'DAMAGED': 'DMG',
                  };
                  return conditionMap[row.condition] || row.condition;
                })() : '')
              : formatConditions(row.acceptableConditions)}
          </div>,
          <div>
            {isHaveList
              ? (row.card?.lang?.toUpperCase() || '')
              : (isWantList ? formatLanguages(row.acceptableLanguages) : (row.card?.lang?.toUpperCase() || ''))}
          </div>
        );
      }

      return {
        key: row.id.toString(),
        card: row.card || ({} as MTGCard),
        components
      };
    }),
  };
};

export const SharedCollectionPage: React.FC<Props> = ({ tab }) => {
  console.log('🟦 SharedCollectionPage: Component function called', { tab });

  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  console.log('🟦 SharedCollectionPage: State read', {
    username,
    currentUser: currentUser ? 'exists' : 'null',
    pathname: window.location.pathname
  });

  const [currentTab, setCurrentTab] = useState<'have-list' | 'want-list'>(tab);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'comfortable' | 'compact'>('comfortable'); // Default to Image View
  const [sortOrder, setSortOrder] = useState<{ sort: string; direction: SortOrder }>({
    sort: 'name',
    direction: 'asc',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Debug: Track component lifecycle
  useEffect(() => {
    console.log('🟩 SharedCollectionPage: Component MOUNTED', { username, tab });
    return () => {
      console.log('🟥 SharedCollectionPage: Component UNMOUNTING', { username, tab });
    };
  }, [username, tab]);

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Fetch data based on current tab
  const { data: haveListData, isLoading: haveListLoading, error: haveListError } = useFetchPublicHaveListQuery(
    {
      username: username || '',
      page: currentPage,
      limit: 100,
      sort: currentTab === 'have-list' ? sortOrder.sort : undefined,
      direction: currentTab === 'have-list' ? sortOrder.direction : undefined,
      search: currentTab === 'have-list' ? searchTerm : undefined,
    },
    { skip: !username || currentTab !== 'have-list' }
  );

  const { data: wantListData, isLoading: wantListLoading, error: wantListError } = useFetchPublicWantListQuery(
    {
      username: username || '',
      page: currentPage,
      limit: 100,
      sort: currentTab === 'want-list' ? sortOrder.sort : undefined,
      direction: currentTab === 'want-list' ? sortOrder.direction : undefined,
      search: currentTab === 'want-list' ? searchTerm : undefined,
    },
    { skip: !username || currentTab !== 'want-list' }
  );

  const isLoading = currentTab === 'have-list' ? haveListLoading : wantListLoading;
  const error = currentTab === 'have-list' ? haveListError : wantListError;
  const data = currentTab === 'have-list' ? haveListData : wantListData;

  // Memoize collectionList to prevent unnecessary re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const collectionList = useMemo(() => {
    return currentTab === 'have-list'
      ? (haveListData?.haveList || [])
      : (wantListData?.wantList || []);
  }, [currentTab, haveListData, wantListData]);

  // Handle sidebar navigation attempts
  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  // Handle Trade button click
  const handleTradeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUser) {
      setShowLoginModal(true);
    } else {
      navigate('/sendcards');
    }
  };

  const handleTabChange = (newTab: 'have-list' | 'want-list') => {
    setCurrentTab(newTab);
    setCurrentPage(1);
    setSearchTerm('');
    navigate(`/collection/${newTab}/${username}`, { replace: true });
  };

  const handleSort = useCallback((key: string) => {
    setSortOrder(prev => ({
      sort: key,
      direction: prev.sort === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  }, []);

  const tableCellsData = useMemo(
    () => getReadOnlyTableCells(
      theme,
      collectionList,
      currentTab === 'want-list',
      currentTab === 'have-list',
      isMobile
    ),
    [theme, collectionList, currentTab, isMobile]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const totalPages = data ? Math.ceil(data.totalRecords / 100) : 1;

  // Handle 404 errors (user not found) - check after all hooks are called
  if (error && typeof error === 'object' && error !== null && 'status' in error && (error as any).status === 404) {
    console.log('🟪 SharedCollectionPage: Rendering 404 page');
    return <UserNotFoundPage />;
  }

  console.log('🟦 SharedCollectionPage: About to render main page', {
    isLoading,
    collectionListLength: collectionList.length,
    hasError: !!error
  });


  // Render card grid for image views
  const renderCardGrid = () => {
    if (viewMode === 'comfortable') {
      return (
        <div className="collection-card-grid">
          {collectionList.flatMap((row) => {
            return Array.from({ length: row.quantity }, (_, index) => {
              const cardKey = `${row.id}-${index}`;
              const scryfallId = row.card?.scryfallID;
              const imageUrl = scryfallId
                ? AssetUrls.cards.getCardImage(scryfallId, 'normal')
                : AssetUrls.cards.defaultBack;
              const displayPrice = getCardDisplayPrice(row);

              return (
                <div key={cardKey} className="collection-card-item">
                  <img
                    className="collection-card-item-image"
                    src={imageUrl}
                    alt={row.cardName || row.card?.name || 'Card'}
                    loading="lazy"
                    onError={(e) => {
                      const img = e.currentTarget;
                      const currentSrc = img.src;

                      // First try backend-provided fallback URLs if available
                      const fallbacks = (row.card as any)?.normal_fallbacks;
                      if (fallbacks && fallbacks.length > 0 && !img.dataset.fallbackAttempted) {
                        img.dataset.fallbackAttempted = 'true';
                        img.src = fallbacks[0];
                        return;
                      }

                      // Otherwise, try different image formats using scryfallId
                      if (scryfallId && !img.dataset.formatFallbackComplete) {
                        // Determine current format from URL
                        if (currentSrc.includes('/normal/')) {
                          // Normal format failed, try small
                          img.src = AssetUrls.cards.getCardImage(scryfallId, 'small');
                          return;
                        } else if (currentSrc.includes('/small/')) {
                          // Small format failed, try large
                          img.src = AssetUrls.cards.getCardImage(scryfallId, 'large');
                          return;
                        } else if (currentSrc.includes('/large/')) {
                          // Large format failed, mark as complete and use placeholder
                          img.dataset.formatFallbackComplete = 'true';
                        }
                      }

                      // If all fallbacks failed, use default card back
                      img.src = AssetUrls.cards.defaultBack;
                    }}
                  />
                  {!scryfallId && (
                    <div className="card-name-overlay">
                      {row.cardName || row.card?.name || 'Unknown Card'}
                    </div>
                  )}
                  {displayPrice !== null && displayPrice !== undefined && (
                    <div className="collection-card-item-points">
                      <Points points={displayPrice} type="small" />
                    </div>
                  )}
                  <div className="collection-card-item-hover-actions">
                    <button
                      className="collection-card-item-trade-btn"
                      onClick={handleTradeClick}
                      aria-label={currentTab === 'want-list' ? 'Send' : 'Trade'}
                    >
                      <img
                        src={AssetUrls.logos.arevakhachIcon}
                        alt="send-icon"
                        style={{ width: 16, height: 16, marginRight: 6 }}
                      />
                      {currentTab === 'want-list' ? 'Send' : 'Trade'}
                    </button>
                  </div>
                </div>
              );
            });
          })}
        </div>
      );
    } else if (viewMode === 'compact') {
      return (
        <div className="collection-card-grid">
          {collectionList.map((row) => {
            const scryfallId = row.card?.scryfallID;
            const imageUrl = scryfallId
              ? AssetUrls.cards.getCardImage(scryfallId, 'normal')
              : AssetUrls.cards.defaultBack;
            const displayPrice = getCardDisplayPrice(row);

            return (
              <div key={row.id} className="collection-card-item">
                <img
                  className="collection-card-item-image"
                  src={imageUrl}
                  alt={row.cardName || row.card?.name || 'Card'}
                  loading="lazy"
                  onError={(e) => {
                    const img = e.currentTarget;
                    const currentSrc = img.src;

                    // First try backend-provided fallback URLs if available
                    const fallbacks = (row.card as any)?.normal_fallbacks;
                    if (fallbacks && fallbacks.length > 0 && !img.dataset.fallbackAttempted) {
                      img.dataset.fallbackAttempted = 'true';
                      img.src = fallbacks[0];
                      return;
                    }

                    // Otherwise, try different image formats using scryfallId
                    if (scryfallId && !img.dataset.formatFallbackComplete) {
                      // Determine current format from URL
                      if (currentSrc.includes('/normal/')) {
                        // Normal format failed, try small
                        img.src = AssetUrls.cards.getCardImage(scryfallId, 'small');
                        return;
                      } else if (currentSrc.includes('/small/')) {
                        // Small format failed, try large
                        img.src = AssetUrls.cards.getCardImage(scryfallId, 'large');
                        return;
                      } else if (currentSrc.includes('/large/')) {
                        // Large format failed, mark as complete and use placeholder
                        img.dataset.formatFallbackComplete = 'true';
                      }
                    }

                    // If all fallbacks failed, use default card back
                    img.src = AssetUrls.cards.defaultBack;
                  }}
                />
                {!scryfallId && (
                  <div className="card-name-overlay">
                    {row.cardName || row.card?.name || 'Unknown Card'}
                  </div>
                )}
                {row.quantity > 1 && (
                  <div className="card-quantity-badge">
                    {row.quantity}
                  </div>
                )}
                {displayPrice !== null && displayPrice !== undefined && (
                  <div className="collection-card-item-points">
                    <Points points={displayPrice} type="small" />
                  </div>
                )}
                <div className="collection-card-item-hover-actions">
                  <button
                    className="collection-card-item-trade-btn"
                    onClick={handleTradeClick}
                    aria-label={currentTab === 'want-list' ? 'Send' : 'Trade'}
                  >
                    <img
                      src={AssetUrls.logos.arevakhachIcon}
                      alt="send-icon"
                      style={{ width: 16, height: 16, marginRight: 6 }}
                    />
                    {currentTab === 'want-list' ? 'Send' : 'Trade'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Dynamic SEO values based on tab
  const pageTitle = currentTab === 'have-list'
    ? `${username}'s MTG Collection - Trade on TradeMagic`
    : `${username}'s Want List - Send Cards on TradeMagic`;

  const pageDescription = currentTab === 'have-list'
    ? `View and trade with ${username}'s Magic: The Gathering card collection. Browse their available cards and start trading on TradeMagic.`
    : `Help ${username} complete their collection. View their MTG want list and send them the cards they need on TradeMagic.`;

  const canonicalUrl = `https://trademagic.gg/collection/${currentTab}/${username?.toLowerCase()}`;

  const breadcrumbLabel = currentTab === 'have-list' ? `${username}'s Collection` : `${username}'s Want List`;

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHelmet
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        keywords={`${username} MTG collection, trade Magic cards with ${username}, ${username} want list, TradeMagic user collection`}
        type="website"
      />

      {/* BreadcrumbList Schema */}
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Collections', url: 'https://trademagic.gg/my-cards' },
          { name: breadcrumbLabel, url: canonicalUrl },
        ]}
      />

      <div className="shared-collection-layout">
      {/* Mobile Menu Overlay - click to close */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="shared-collection-content-wrapper">
        <LeftNavigationBar
          currentTab={{ path: '/my-cards', Component: <></> }}
          isPublicMode={true}
          onNavigationAttempt={handleNavigationAttempt}
          isAuthenticated={!!currentUser}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          onMenuClose={() => setIsMobileMenuOpen(false)}
        />

        <div className="shared-collection-main">
        <div className="shared-collection-header">
          {/* Mobile Hamburger Button */}
          {isMobile && (
            <button
              className={`mobile-hamburger ${isMobileMenuOpen ? 'open' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle navigation menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          )}

          <h1 className="shared-collection-title">
            {currentTab === 'have-list' ? `${username}'s Collection` : `${username}'s Want List`}
          </h1>
          <p className="shared-collection-subtitle">
            {currentTab === 'have-list'
              ? `The cards in ${username}'s trade binder.`
              : `Cards ${username} would like to receive.`}
            {!currentUser && (
              <>
                {' '}
                <a
                  href="/welcome"
                  style={{ color: '#DD5BEA', textDecoration: 'none' }}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/welcome');
                  }}
                >
                  Login
                </a>
                {' '}to start a trade.
              </>
            )}
          </p>
        </div>

        <div className="shared-collection-tabs">
          <button
            className={`shared-collection-tab ${currentTab === 'want-list' ? 'active' : ''}`}
            onClick={() => handleTabChange('want-list')}
          >
            <img
              src={AssetUrls.icons.misc.heart}
              alt="Want List"
              style={{ width: '16px', height: '16px', marginRight: '8px' }}
            />
            Want List
          </button>
          <button
            className={`shared-collection-tab ${currentTab === 'have-list' ? 'active' : ''}`}
            onClick={() => handleTabChange('have-list')}
          >
            <img
              src={AssetUrls.icons.misc.have}
              alt="Have List"
              style={{ width: '16px', height: '16px', marginRight: '8px' }}
            />
            Have List
          </button>
        </div>

        <div className="shared-collection-controls">
          <div className="shared-collection-search">
            <TextInputField
              leftComponent={
                <FontAwesomeIcon
                  size="sm"
                  icon={'magnifying-glass'}
                  color="#DD5BEA"
                />
              }
              value={searchTerm}
              placeholder="Search cards..."
              onChange={setSearchTerm}
            />
          </div>

          <select
            className="shared-collection-dropdown"
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value as 'list' | 'comfortable' | 'compact')}
          >
            <option value="comfortable">Image View</option>
            <option value="list">List View</option>
            <option value="compact">Images - Compact</option>
          </select>

          <select
            className="shared-collection-dropdown"
            value={`${sortOrder.sort}-${sortOrder.direction}`}
            onChange={(e) => {
              const [sort, direction] = e.target.value.split('-');
              setSortOrder({ sort, direction: direction as SortOrder });
              setCurrentPage(1);
            }}
          >
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
            <option value="value-asc">Price (Low to High)</option>
            <option value="value-desc">Price (High to Low)</option>
          </select>
        </div>

        <div className="shared-collection-content">
          {isLoading ? (
            <div className="collection-loading-overlay">
              <div className="loading-spinner" />
            </div>
          ) : collectionList.length === 0 ? (
            <div className="shared-collection-empty">
              <div className="empty-icon">📦</div>
              <h2 className="empty-title">No Cards Yet</h2>
              <p className="empty-message">
                {username} hasn't added any cards to their {currentTab === 'have-list' ? 'Have' : 'Want'} List yet. Check back later!
              </p>
            </div>
          ) : (
            <>
              {viewMode === 'list' ? (
                <CardsTable
                  isLoading={isLoading}
                  cellsData={tableCellsData}
                  orderBy={sortOrder.sort}
                  order={sortOrder.direction}
                  rowDividerType="color-mix"
                  onHeaderSortPress={handleSort}
                />
              ) : (
                renderCardGrid()
              )}
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  showFirstLast={true}
                />
              )}
            </>
          )}
        </div>
        </div>
      </div>

      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        collectionOwnerUsername={username || ''}
      />

      <Footer />
      </div>
    </>
  );
};

export default SharedCollectionPage;
